import { Metric } from '../../../../dashboard-config/dashboard-config.model';
import {
  companiesCountProfile,
  companiesRevenueProfile,
  economicParticipantsProfile,
  employedProfile,
  gvaProfile,
} from '../../../../dashboard-config/metrics/metric-profile-implementations';
import {
  ComparisonMetricProfile,
  comparisonCompaniesCountProfile,
  comparisonCompaniesRevenueProfile,
  comparisonEconomicParticipantProfile,
  comparisonEmployedProfile,
  comparisonGvaProfile,
} from '../comparison-metric.profile';
import { DashboardComparisonIndustriesPopupView } from './dashboard-comparison-industries.definition';
import { KPI_NAME_KEY } from '../../../../dashboard-config/kpis/kpi-data-transformers';

/**
 * Industries-shell metric profile: shared comparison pipeline core +
 * industries presentation (first-class, not a bag on {@link ComparisonMetricProfile}).
 */
export type ComparisonIndustriesMetricProfile = Omit<
  ComparisonMetricProfile,
  'popup'
> & {
  metricId: Metric;
  mainBarLabelHeight?: number;
  popup: DashboardComparisonIndustriesPopupView;
};

function fromShared(
  shared: ComparisonMetricProfile,
  industries: {
    mainBarLabelHeight?: number;
    /** Industries heading when it differs from the shared metric heading. */
    headingPrefix?: string;
    popup: DashboardComparisonIndustriesPopupView;
  },
): ComparisonIndustriesMetricProfile {
  const { popup: sharedPopup, ...core } = shared;
  return {
    ...core,
    metricId: sharedPopup.metricId,
    headingPrefix: industries.headingPrefix ?? shared.headingPrefix,
    mainBarLabelHeight: industries.mainBarLabelHeight,
    popup: industries.popup,
  };
}

export const comparisonIndustriesCompaniesCountProfile: ComparisonIndustriesMetricProfile =
  fromShared(comparisonCompaniesCountProfile, {
    popup: {
      metricName: companiesCountProfile.labels.entityLabel!,
      metricLabel: companiesCountProfile.labels.shortLabel,
    },
  });

export const comparisonIndustriesCompaniesRevenueProfile: ComparisonIndustriesMetricProfile =
  fromShared(comparisonCompaniesRevenueProfile, {
    mainBarLabelHeight: 100,
    popup: {
      metricName: companiesRevenueProfile.labels.entityLabel!,
      metricLabel: companiesRevenueProfile.labels.shortLabel,
    },
  });

export const comparisonIndustriesGvaProfile: ComparisonIndustriesMetricProfile =
  fromShared(comparisonGvaProfile, {
    mainBarLabelHeight: 100,
    popup: {
      metricName: gvaProfile.labels.entityLabel!,
      metricNameKey: KPI_NAME_KEY,
      metricLabel: gvaProfile.labels.shortLabel,
      showMetricTypeField: false,
    },
  });

export const comparisonIndustriesEconomicParticipantProfile: ComparisonIndustriesMetricProfile =
  fromShared(comparisonEconomicParticipantProfile, {
    popup: {
      metricName: economicParticipantsProfile.labels.entityLabel!,
      metricLabel: economicParticipantsProfile.labels.shortLabel,
      detailSlot: 'economic_participants',
    },
  });

export const comparisonIndustriesEmployedProfile: ComparisonIndustriesMetricProfile =
  fromShared(comparisonEmployedProfile, {
    popup: {
      metricName: employedProfile.labels.entityLabel!,
      metricLabel: employedProfile.labels.shortLabel,
    },
  });
